// Operator session persistence.
//
// Mirrors the in-memory-only posture of the Flutter Chamber's
// `WebTokenStorage` (no Service-Worker / IndexedDB persistence) but
// still has to survive a React Router navigation. `sessionStorage` is
// the smallest hammer that does both: cleared when the tab closes,
// not shared with other origins, not exposed to background workers.

const STORAGE_KEY = 'mlm.admin.session'

export interface StoredSession {
  id: string
  // Unix seconds (matches the backend's expires_time field).
  expiresAt: number
}

export function loadSession(): StoredSession | null {
  let raw: string | null
  try {
    raw = sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>
    if (
      typeof parsed.id === 'string' && parsed.id.length > 0 &&
      typeof parsed.expiresAt === 'number' && Number.isFinite(parsed.expiresAt)
    ) {
      return { id: parsed.id, expiresAt: parsed.expiresAt }
    }
    return null
  } catch {
    return null
  }
}

export function saveSession(session: StoredSession): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Quota errors / private-mode failures: surface via the next
    // loadSession() returning null. The auth flow is already prepared
    // for that branch (re-auth is just another sign-in).
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
